import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useOrganization } from '../contexts/OrganizationContext';
import { excludeTestAccountUsers } from '../lib/people/excludeTestAccounts';
import type { User } from '../types/firestore';

/**
 * True when HR has positively recorded that this person does not currently
 * work here.
 *
 * `users.isActive` says whether the LOGIN is enabled, which is a different
 * question and one nothing flips on termination — production carried an
 * active login for someone who had resigned, so they appeared in every
 * assignee dropdown in the app. HR is the reliable signal, and the
 * mirrorRoleTags trigger projects a single `isCurrentEmployee` boolean onto
 * the user doc precisely so non-HR surfaces (which cannot read employee
 * records) can honour it without learning anything else about the person.
 *
 * A MISSING flag is not "departed" — it means there is no employee record to
 * judge by. Shared mailboxes (service@onpointgen.com) and service accounts
 * are legitimate assignees and must keep working. Only an explicit `false`
 * excludes someone.
 */
export function isNotCurrentEmployeeUser(data: Record<string, unknown>): boolean {
  return data.isCurrentEmployee === false;
}

export interface UseActiveUsersOptions {
  /**
   * Use for a DROPDOWN people choose from: test logins are hidden. Default false = everyone, which is what name
   * lookups of already-assigned records need (a record a test login touched must still show its name).
   */
  readonly pickableOnly?: boolean;
}

/**
 * Fetches all active users in the organization, sorted by displayName,
 * EXCLUDING anyone HR records as having left.
 *
 * Used for assignee dropdowns across the app. For historical name resolution
 * — where a departed person's name must still render — use `useAllOrgUsers`.
 *
 * @returns Array of active User records
 * @example
 * const { data: users = [] } = useActiveUsers();
 */
export function useActiveUsers({ pickableOnly = false }: UseActiveUsersOptions = {}) {
  const { currentOrganization } = useOrganization();
  // `select` shapes the result per observer on the SAME cache entry: one read, no key change.
  const select = useCallback((users: User[]) => (pickableOnly ? excludeTestAccountUsers(users) : users), [pickableOnly]);

  return useQuery({
    select,
    queryKey: ['activeUsers', currentOrganization?.organizationId],
    queryFn: async (): Promise<User[]> => {
      const q = query(
        collection(db, 'users'),
        where('organizationId', '==', currentOrganization!.organizationId),
        where('isActive', '==', true),
        orderBy('displayName')
      );
      const snap = await getDocs(q);
      return snap.docs
        .filter((d) => !isNotCurrentEmployeeUser(d.data()))
        .map((d) => ({ userId: d.id, ...d.data() } as User));
    },
    enabled: !!currentOrganization?.organizationId,
    staleTime: 10 * 60 * 1000, // 10 min — user list changes rarely
  });
}

/** Sort by displayName; a login with NO displayName sorts last (it must still be returned: see useAllOrgUsers). */
const byDisplayName = (a: User, b: User): number => {
  const an = typeof a.displayName === 'string' ? a.displayName : '';
  const bn = typeof b.displayName === 'string' ? b.displayName : '';
  if (!an !== !bn) return an ? -1 : 1;
  return an.localeCompare(bn);
};

/**
 * Fetches ALL users in the organization, including deactivated ones.
 * Use for resolving names on historical records (e.g. past meeting
 * attendees, audit trails) where the referenced person may have since
 * left — unlike useActiveUsers, this must not silently drop them, or a
 * former employee's UID renders raw instead of falling back to their name.
 *
 * No server-side `orderBy('displayName')`: Firestore drops documents that lack the ordered field, so a login with no displayName
 * vanished from every consumer. Sorted here instead (logins without a name last).
 *
 * @param options.enabled Set false to skip the read (e.g. a closed modal). Default true.
 * @returns Array of all User records (active + inactive)
 * @example
 * const { data: users = [] } = useAllOrgUsers();
 */
export function useAllOrgUsers({ enabled = true }: { readonly enabled?: boolean } = {}) {
  const { currentOrganization } = useOrganization();

  return useQuery({
    queryKey: ['allOrgUsers', currentOrganization?.organizationId],
    queryFn: async (): Promise<User[]> => {
      const q = query(collection(db, 'users'), where('organizationId', '==', currentOrganization!.organizationId));
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ userId: d.id, ...d.data() } as User)).sort(byDisplayName);
    },
    enabled: enabled && !!currentOrganization?.organizationId,
    staleTime: 10 * 60 * 1000,
  });
}
